import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { PartnerGetAllApiArg, usePartnerGetAllQuery } from '../../common/redux/api/web-api'
import { SInput } from '../../support/features/charge-point-capi-details-widget/charge-point-capi-details-widget.style'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import Text from '../../ui/Text'
import { Button } from '../../common/components/Button'
import { CreatePartnerModal } from '../create-partner-modal'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../table'
import { WidgetLayout } from '../widget-layout'
import { ChargersPagination } from '../chargers-pagination'
import useText from '../../common/hooks/useText'

const SSearchForm = styled.div`
    padding-bottom: 20px;
    width: 100%;
    display: flex;
    justify-content: space-between;

    @media (min-width: 990px) {
        width: 55%;
    }
`

const SSearchContainer = styled.form`
    width: 100%;
    display: flex;
    justify-content: space-between;
`

const SRow = styled.div`
    display: flex;
    justify-content: space-between;
    max-width: 200px;
    margin-bottom: 40px;
`

export type PartnersListWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
}

type PartnerFilter = Pick<PartnerGetAllApiArg, 'name' | 'email'>

const partnerFilterInitialValues: PartnerFilter = { name: '', email: '' }

export const PartnersListWidget = memo(function PartnersListWidget({
    className,
    style,
}: PartnersListWidgetProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const [pageSize] = useState(50)
    const [currentPage, setCurrentPage] = useState(0)
    const [filter, setFilter] = useState<PartnerFilter>({
        name: '',
        email: '',
    })

    const {
        currentData: partnersData,
        refetch: refetchPartners,
        isFetching: gettingPartners,
    } = usePartnerGetAllQuery({ noOfItems: pageSize, excludeOrphans: false, page: currentPage, ...filter })
    const [isCreatePartnerModalOpened, setIsCreatePartnerModalOpened] = useState(false)

    const { handleSubmit, register, reset } = useForm<PartnerFilter>({
        defaultValues: partnerFilterInitialValues,
    })

    const onSubmit = (data: PartnerFilter) => {
        setCurrentPage(0)
        setFilter(data)
    }

    const handleClearSearch = () => {
        reset()
        setFilter(partnerFilterInitialValues)
    }

    return (
        <WidgetLayout className={cn(className)} style={style} title={text('lblPartnersWidget')} data-testid="partners">
            {isCreatePartnerModalOpened && (
                <BootstrapModalLayout>
                    <CreatePartnerModal
                        onAddPartner={refetchPartners}
                        close={() => setIsCreatePartnerModalOpened(false)}
                    />
                </BootstrapModalLayout>
            )}
            <SSearchContainer onSubmit={handleSubmit(onSubmit)}>
                <SSearchForm>
                    <SInput
                        {...register('name')}
                        className="margin-right-small"
                        data-testid="search-name-field"
                        placeholder={text('lblName')}
                    />
                    <SInput {...register('email')} data-testid="search-email-field" placeholder={text('lblEmail')} />
                </SSearchForm>
                <SRow>
                    <Button
                        label={text('btnSearch')}
                        minWidth="95px"
                        data-testid="btn-search"
                        size="sm"
                        className="margin-right-small"
                        type="submit"
                    />
                    <Button label={text('btnClear')} minWidth="95px" size="sm" onClick={handleClearSearch} />
                </SRow>
            </SSearchContainer>

            <Table>
                <TableHeader>
                    <TableRow data-testid="table-header-row">
                        <TableHeaderCell data-testid="name">
                            <Text code="lblName" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="desc">
                            <Text code="lblAddress" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="email">
                            <Text code="lblEmail" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="phone">
                            <Text code="lblPhone" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="host-name">
                            <Text code="lblHostName" />
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!gettingPartners
                        ? partnersData?.items?.map((partner) => (
                              <TableRow
                                  className={'clickable'}
                                  key={`row-${'partner-'}${partner.id}`}
                                  data-testid={`row-${'partner-'}${partner.id}`}
                                  onClick={() => navigate(`/partner/${partner.id}`)}
                              >
                                  <TableBodyCell data-testid={'name-' + partner.id}>{partner.name}</TableBodyCell>
                                  <TableBodyCell data-testid={'desc-' + partner.id}>
                                      {partner.description}
                                  </TableBodyCell>
                                  <TableBodyCell data-testid={'email-' + partner.id}>{partner.email}</TableBodyCell>
                                  <TableBodyCell data-testid={'phone-' + partner.id}>{partner.phone}</TableBodyCell>
                                  <TableBodyCell data-testid={'host-name-' + partner.id}>
                                      {partner.hostName}
                                  </TableBodyCell>
                              </TableRow>
                          ))
                        : null}
                </TableBody>
            </Table>
            {(partnersData?.totalCount ?? 0 > 50) && (
                <ChargersPagination
                    pageIndex={currentPage}
                    setPageIndex={(pageIndex: number) => {
                        window.scrollTo(0, 0)
                        setCurrentPage(pageIndex)
                    }}
                    totalCount={partnersData?.totalCount ?? 0}
                    pageSize={pageSize}
                    displayedChargers={pageSize}
                />
            )}
            {gettingPartners && <div className="shimmer-container shimmer-large"></div>}
        </WidgetLayout>
    )
})
